import {
  BreakpointObserver,
  Breakpoints,
  BreakpointState
} from "@angular/cdk/layout";
import { Component, inject } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterModule
} from "@angular/router";
import { filter, map } from "rxjs/operators";
import { MenuItem, ToastMessageOptions } from "primeng/api";
import { deployment, environment } from "../../../environments/environment";
import { Role } from "../../_models/role";
import { LoggingService, LogLevel } from "../../_services/logging.service";
import { UserService } from "../../_services/user.service";
import { ReviewSessionService } from "../../_services/review-session.service";
import { ToastModule } from "primeng/toast";
import { TrackingConsentComponent } from "../../tracking-consent/tracking-consent.component";
import { MenubarModule } from "primeng/menubar";
import { MainMenuSearchComponent } from "../main-menu-search/main-menu-search.component";
import { MessageModule } from "primeng/message";
import { LoginComponent } from "../../login/login.component";
import { ScrollTopModule } from "primeng/scrolltop";
import { FooterComponent } from "../footer/footer.component";
import { CommonModule } from "@angular/common";
import { BackendStatusNotificationComponent } from "../../shared/backend-status-notification/backend-status-notification.component";
import { ErrorCardComponent } from "../../errors/error-card/error-card.component";
import { ChunkLoadRecoveryService } from "../../_services/chunk-load-recovery.service";
import { FeatureAccessService } from "../../_services/feature-access.service";

@Component({
  selector: "app-main",
  templateUrl: "./main.component.html",
  styleUrls: ["./main.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    BackendStatusNotificationComponent,
    ErrorCardComponent,
    FooterComponent,
    LoginComponent,
    TrackingConsentComponent,
    ToastModule,
    MainMenuSearchComponent,
    MenubarModule,
    MessageModule,
    RouterModule,
    ScrollTopModule,
    ToastModule
  ]
})
export class MainComponent {
  userService = inject(UserService);
  /** Reviewer-link session (#226). Used to hide login-oriented chrome from anonymous reviewers. */
  reviewSession = inject(ReviewSessionService);
  /**
   * Set only when a chunk-load recovery reload has already been tried and the
   * chunk still failed (#366). Rendering the card here rather than navigating
   * keeps the shell and navigation usable.
   */
  chunkRecovery = inject(ChunkLoadRecoveryService);
  private featureAccess = inject(FeatureAccessService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private breakpointObserver = inject(BreakpointObserver);
  private loggingService = inject(LoggingService);

  /**
   * A completed navigation proves the app can load chunks again, so any
   * leftover chunk-load error card (#366) can be dismissed. A failed chunk
   * load never produces a NavigationEnd, so this only clears the card when a
   * navigation genuinely succeeded. takeUntilDestroyed() is called here, in
   * a field initializer, so it runs in an injection context and this needs
   * no OnDestroy.
   *
   * This does NOT cover every dismissal path, which is why the template also
   * binds `(dismissed)="chunkRecovery.dismiss()"` on the error card itself:
   * the card's Dismiss button navigates via `[routerLink]="['/home']"`, but a
   * failed lazy navigation leaves the URL on its origin route, so clicking
   * Dismiss while already on that route is a same-URL navigation. Angular's
   * `onSameUrlNavigation` defaults to 'ignore', so that emits
   * NavigationSkipped, not NavigationEnd - this subscription would never
   * fire and the card would silently stay put. The `dismissed` output
   * clears the signal directly, independent of routing, so it still works
   * in exactly that case.
   */
  private readonly clearChunkErrorOnNavigation = this.router.events
    .pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntilDestroyed()
    )
    .subscribe(() => this.chunkRecovery.dismiss());

  static readonly ROUTE_DATA_BREADCRUMB = "breadcrumb";
  readonly home = { icon: "fa fa-compass", url: "/home" };
  breadCrumbs: MenuItem[] = [this.home];
  items: MenuItem[];

  /**
   * True while the viewport is too narrow to host the search inside the
   * menubar, so the template renders it in its own row underneath instead.
   *
   * This has to be a signal, not a plain field. It used to be one, assigned
   * from two BreakpointObserver subscriptions, and nothing marked this view
   * dirty when they fired: the field flipped but the template kept rendering
   * whatever breakpoint had been active when it was first created, so the
   * search only ever landed in the right place on a full page load and never
   * followed a resize. A signal write notifies the consuming view directly, so
   * the render no longer depends on some unrelated event happening to schedule
   * a change-detection pass.
   *
   * One query is enough, and is what makes the state unambiguous: the two
   * observers this replaces each wrote only on their matching side
   * (`if (result.matches)`), so the value depended on which fired last.
   * XSmall|Small is exactly the complement of Medium|Large|XLarge (they meet at
   * 960px), so "wrap" is simply "a narrow breakpoint matches".
   */
  readonly wrapSearch = toSignal(
    this.breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.Small])
      .pipe(map((result: BreakpointState) => result.matches)),
    { initialValue: false }
  );

  requiresAuth: Boolean = environment.homeRequiresAuth;
  loginMessage: ToastMessageOptions = environment.loginMessage;
  apiUrl: string = deployment.apiPath;

  constructor() {
    this.loggingService.log(
      "MainComponent",
      "requiresAuth: " + this.requiresAuth,
      LogLevel.DEBUG
    );
    this.createMenuForUser();
  }

  /**
   * The "Lipidomes" group: the two cross-dataset views, each gated on its own feature
   * (#438), so an early-access view appears here only for the cohort that may open it.
   *
   * The parent is hidden when neither child is visible. PrimeNG would otherwise render a
   * caret onto an empty dropdown -- a nav entry that leads nowhere, which is worse than no
   * entry at all. It carries no routerLink of its own because there is no combined view to
   * route to; it exists to group the two.
   */
  private lipidomesMenuItem(): MenuItem {
    const children: MenuItem[] = [
      {
        label: "Core Lipidomes",
        icon: "fa fa-layer-group",
        routerLink: ["/core-lipidomes"],
        visible: this.featureAccess.isEnabled("coreLipidomes")
      },
      {
        label: "Lipidome Landscape",
        icon: "fa fa-mountain-sun",
        routerLink: ["/lipidome-landscape"],
        visible: this.featureAccess.isEnabled("lipidomeLandscape")
      }
    ];
    return {
      label: "Lipidomes",
      icon: "fa fa-layer-group",
      items: children,
      visible: children.some((c) => c.visible)
    };
  }

  private createMenuForUser() {
    this.userService.isAuthenticated$.subscribe(observable => {
      if (observable) {
        this.loggingService.log(
          "MainComponent",
          "User is authenticated.",
          LogLevel.DEBUG
        );
        this.items = [
          {
            label: "Home",
            icon: "fa fa-compass",
            routerLink: ["/home"],
            visible: this.featureAccess.isEnabled("home")
          },
          {
            label: "Lipids",
            icon: "fa fa-search",
            routerLink: ["/lipid"],
            visible: this.featureAccess.isEnabled("lipid")
          },
          this.lipidomesMenuItem(),
          {
            label: "Studies",
            icon: "fa fa-vials",
            routerLink: ["/study"],
            visible: this.featureAccess.isEnabled("study")
          },
          {
            label: "Datasets",
            icon: "fa fa-archive",
            routerLink: ["/dataset"],
            visible: this.featureAccess.isEnabled("dataset")
          },
          {
            label: "mzTab-M",
            icon: "fa fa-file-alt",
            routerLink: ["/mztabm"],
            visible: this.featureAccess.isEnabled("mztab")
          },
          {
            label: "News",
            icon: "fas fa-newspaper",
            routerLink: ["/news"],
            visible: this.featureAccess.isEnabled("news")
          },
          {
            label: "Help",
            icon: "fa fa-solid fa-circle-question",
            routerLink: ["/guide"],
            visible: this.featureAccess.isEnabled("docs")
          },
          {
            label: "About",
            icon: "fa fa-solid fa-circle-info",
            routerLink: ["/info"],
            visible: this.featureAccess.isEnabled("about")
          },
          {
            label: "API",
            icon: "fa fa-solid fa-scroll",
            url: this.apiUrl,
            visible: this.featureAccess.isEnabled("api")
          }
        ];
        if (
          this.userService.hasRole(Role.Curator) ||
          this.userService.hasRole(Role.Admin)
        ) {
          this.items.push({
            label: "Curate",
            icon: "fas fa-highlighter",
            routerLink: ["/curate"],
            visible: this.featureAccess.isEnabled("curate")
          });
        }
        if (
          this.userService.hasRole(Role.Curator) ||
          this.userService.hasRole(Role.Admin)
        ) {
          // A curator (without the admin role) can only reach the submissions
          // page inside /admin (see AdminComponent's megamenu filtering); the
          // nav entry still needs to be discoverable for them to get there.
          this.items.push({
            label: "Admin",
            icon: "fas fa-tools",
            routerLink: ["/admin"],
            visible: this.featureAccess.isEnabled("admin")
          });
        }
        if (this.userService.hasRole(Role.User)) {
          this.items.push({
            label: "Submissions",
            icon: "fas fa-cloud-upload-alt",
            routerLink: ["/submit"],
            visible: this.featureAccess.isEnabled("submit")
          });
        }
        this.items.push({
          label: "User",
          icon: "fas fa-user",
          routerLink: ["/login"],
          visible: this.featureAccess.isEnabled("login")
        });
      } else {
        this.loggingService.log(
          "MainComponent",
          "User is not authenticated.",
          LogLevel.DEBUG
        );
        if (this.requiresAuth) {
          this.items = [
            {
              label: "Home",
              icon: "fa fa-compass",
              routerLink: ["/"],
              // Every other nav entry keeps PrimeNG's `{ exact: false }`
              // default so a detail route (/lipid/LM-1234) keeps its section
              // highlighted. '/' is the exception: a non-exact match against
              // it is a prefix match on every URL in the app, which would
              // leave Home lit permanently.
              routerLinkActiveOptions: { exact: true },
              visible: this.featureAccess.isEnabled("home")
            },
            {
              label: "API",
              icon: "fa fa-solid fa-scroll",
              url: this.apiUrl,
              visible: this.featureAccess.isEnabled("api")
            },
            {
              label: "Login",
              icon: "fa fa-sign-in-alt",
              routerLink: ["/login"],
              visible: this.featureAccess.isEnabled("login")
            }
          ];
        } else {
          this.items = [
            {
              label: "Home",
              icon: "fa fa-compass",
              routerLink: ["/home"],
              visible: this.featureAccess.isEnabled("home")
            },
            {
              label: "Lipids",
              icon: "fa fa-search",
              routerLink: ["/lipid"],
              visible: this.featureAccess.isEnabled("lipid")
            },
            this.lipidomesMenuItem(),
            {
              label: "Studies",
              icon: "fa fa-vials",
              routerLink: ["/study"],
              visible: this.featureAccess.isEnabled("study")
            },
            {
              label: "Datasets",
              icon: "fa fa-archive",
              routerLink: ["/dataset"],
              visible: this.featureAccess.isEnabled("dataset")
            },
            {
              label: "News",
              icon: "fas fa-newspaper",
              routerLink: ["/news"],
              visible: this.featureAccess.isEnabled("news")
            },
            {
              label: "Help",
              icon: "fa fa-solid fa-circle-question",
              routerLink: ["/guide"],
              visible: this.featureAccess.isEnabled("docs")
            },
            {
              label: "About",
              icon: "fa fa-solid fa-circle-info",
              routerLink: ["/info"],
              visible: this.featureAccess.isEnabled("about")
            },
            {
              label: "API",
              icon: "fa fa-solid fa-scroll",
              url: this.apiUrl,
              visible: this.featureAccess.isEnabled("api")
            },
            {
              label: "Login",
              icon: "fa fa-sign-in-alt",
              routerLink: ["/login"],
              visible: this.featureAccess.isEnabled("login")
            }
          ];
        }
      }
    });
  }
}
